import { useEffect, useMemo, useRef } from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import AnnotationController from './AnnotationController';
import {
  ANNOTATION_TOPIC,
  decodeAnnotationMessage,
} from './annotationProtocol';

/**
 * Owns an AnnotationController wired to LiveKit room data packets.
 */
export default function useAnnotationSync() {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const controllerRef = useRef(null);

  const controller = useMemo(() => {
    const c = new AnnotationController();
    controllerRef.current = c;
    return c;
  }, []);

  useEffect(() => {
    if (!room) return undefined;

    const identity = localParticipant?.identity || room.localParticipant?.identity;
    controller.configure({
      localIdentity: identity || null,
      send: async (data, reliable) => {
        const pub = room.localParticipant;
        if (!pub) return;
        try {
          await pub.publishData(data, {
            reliable: Boolean(reliable),
            topic: ANNOTATION_TOPIC,
          });
        } catch (err) {
          // Retry once without topic for older SFUs (still JSON-typed).
          try {
            await pub.publishData(data, { reliable: Boolean(reliable) });
          } catch {
            console.warn('[annotation] publishData failed', err);
          }
        }
      },
    });

    const onData = (payload, participant, _kind, topic) => {
      if (topic && topic !== ANNOTATION_TOPIC) return;
      const msg = decodeAnnotationMessage(payload);
      if (!msg) return;
      // Extra guard: ignore packets from self if identity matches
      const from = msg.fromIdentity || participant?.identity;
      if (from && identity && from === identity) return;
      controller.applyRemote({ ...msg, fromIdentity: from });
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [room, controller, localParticipant?.identity]);

  useEffect(() => () => controller.dispose(), [controller]);

  return controller;
}
